import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Chip
} from '@mui/material';
import { LibraryBooks as LibraryIcon } from '@mui/icons-material';
import { chequeTemplateService } from '../../../services/templates/chequeTemplateService';
import { COUNTRIES } from '../../../constants/countries';

const TemplateImportDialog = ({ open, onClose, onImport }) => {
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['templateLibrary', country, search],
    queryFn: async () => {
      const result = await chequeTemplateService.searchLibraryTemplates({
        country: country || undefined,
        search: search || undefined,
        pageSize: 50
      });
      return result;
    },
    enabled: open
  });

  const templates = data?.data || [];
  const selected = templates.find(t => t.id === selectedId);

  const handleImport = () => {
    if (selected) {
      onImport(selected);
      handleClose();
    }
  };

  const handleClose = () => {
    setCountry('');
    setSearch('');
    setSelectedId(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        <LibraryIcon sx={{ color: '#2563eb' }} />
        Import from Template Library
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Template dimensions are based on information available to us. Please verify and adjust
          based on your printer settings and actual cheque dimensions.
        </Alert>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Country"
            value={country}
            onChange={(e) => { setCountry(e.target.value); setSelectedId(null); }}
            sx={{ flex: 1 }}
          >
            <MenuItem value="">All Countries</MenuItem>
            {COUNTRIES.map(c => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search bank name"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedId(null); }}
            sx={{ flex: 1 }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {country || search ? 'No templates found matching your search.' : 'No templates available in the library yet.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            {templates.map((t) => (
              <ListItemButton
                key={t.id}
                selected={selectedId === t.id}
                onClick={() => setSelectedId(t.id)}
                sx={{
                  borderBottom: '1px solid #f1f5f9',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    borderLeft: '3px solid #2563eb'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {t.bankName}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        — {t.templateName}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip label={t.country} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t.templateHeight} x {t.templateWidth} cm
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!selected}
          sx={{ backgroundColor: '#2563eb', '&:hover': { backgroundColor: '#1d4ed8' } }}
        >
          Import Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateImportDialog;
